import React, { useState, useEffect, MouseEvent } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import MobileNav from '../MobileNav';

type NavItem = {
  label: string;
  href: string;
};

interface HeaderProps {
  isInDarkSection?: boolean;
}

interface RippleState {
  x: number;
  y: number;
  key: number;
}

const Header: React.FC<HeaderProps> = ({ isInDarkSection = false }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [ripples, setRipples] = useState<{ [key: string]: RippleState | null }>({});

  const navItems: NavItem[] = [
    { label: 'About', href: '#about' },
    { label: 'Experience', href: '#experience' },
    { label: 'Projects', href: '#projects' },
    { label: 'Skills', href: '#skills' },
    { label: 'Contact', href: '#contact' },
  ];

  const mobileNavItems = [{ label: 'Home', href: '#home' }, ...navItems];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);

      // Determine active section
      const sections = ['home', 'about', 'experience', 'projects', 'skills', 'contact'];
      for (const section of sections.reverse()) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 100) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigateTo = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleLinkClick = (event: MouseEvent<HTMLAnchorElement>, href: string) => {
    event.preventDefault();

    // Create ripple effect
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const key = Date.now();

    setRipples(prev => ({
      ...prev,
      [href]: { x, y, key }
    }));

    // Clear ripple after animation
    setTimeout(() => {
      setRipples(prev => ({
        ...prev,
        [href]: null
      }));
    }, 600);

    navigateTo(href);
  };

  return (
    <header
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isInDarkSection
          ? 'bg-neutral-900/95 backdrop-blur-md'
          : 'bg-transparent'
      }`}
    >
      <MobileNav items={mobileNavItems} isScrolled={isScrolled} onNavigate={navigateTo} isInDarkSection={isInDarkSection} />
      <nav className="max-w-6xl mx-auto px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <motion.a
            href="#home"
            onClick={(event) => handleLinkClick(event, '#home')}
            className="flex items-center gap-3 group"
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.2 }}
          >
            <img
              src={`${process.env.PUBLIC_URL}/logo.png`}
              alt="Anthony Abid Logo"
              className="w-10 h-10 object-contain"
            />
          </motion.a>

          {/* Desktop navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navItems.map((item) => {
              const isActive = activeSection === item.href.slice(1);
              const ripple = ripples[item.href];
              return (
                <motion.a
                  key={item.label}
                  href={item.href}
                  onClick={(event) => handleLinkClick(event, item.href)}
                  className={`relative px-4 py-2 text-sm transition-all duration-200 rounded-lg overflow-hidden ${
                    isInDarkSection
                      ? isActive
                        ? 'text-matcha-400 bg-neutral-800'
                        : 'text-neutral-400 hover:text-white hover:bg-neutral-800'
                      : isActive
                        ? 'text-matcha-700 bg-matcha-50'
                        : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                  whileHover={{ y: -1 }}
                  transition={{ duration: 0.2 }}
                >
                  {/* Ripple effect */}
                  <AnimatePresence>
                    {ripple && (
                      <motion.span
                        key={ripple.key}
                        className={`absolute rounded-full pointer-events-none ${
                          isInDarkSection ? 'bg-matcha-400/30' : 'bg-matcha-500/30'
                        }`}
                        style={{
                          left: ripple.x,
                          top: ripple.y,
                        }}
                        initial={{ width: 0, height: 0, x: 0, y: 0, opacity: 1 }}
                        animate={{
                          width: 120,
                          height: 120,
                          x: -60,
                          y: -60,
                          opacity: 0,
                        }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.6, ease: 'easeOut' }}
                      />
                    )}
                  </AnimatePresence>

                  <span className="relative z-10">{item.label}</span>

                  {/* Active indicator */}
                  {isActive && (
                    <motion.div
                      layoutId="activeNav"
                      className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full ${
                        isInDarkSection ? 'bg-matcha-400' : 'bg-matcha-500'
                      }`}
                      initial={false}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    />
                  )}
                </motion.a>
              );
            })}
          </div>

        </div>
      </nav>
    </header>
  );
};

export default Header;
